// client/src/components/Cashflow/CashflowPage.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tooltip,
  Divider,
  Stack,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import EventNoteIcon from '@mui/icons-material/EventNote';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from 'recharts';
import api from '../../services/api';

// ─── helpers ──────────────────────────────────────────────────────────────────

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(value ?? 0);

const formatDate = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const formatMonth = (monthStr) => {
  const [year, month] = monthStr.split('-');
  return new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });
};

const todayStr = new Date().toISOString().split('T')[0];

// ─── sub-components ───────────────────────────────────────────────────────────

/** Single upcoming-event row card */
function UpcomingEventCard({ event }) {
  const isIncome  = event.type === 'income';
  const isOverdue = event.date < todayStr;

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1,
        borderLeft: '4px solid',
        borderLeftColor: isIncome ? 'success.main' : 'error.main',
        opacity: isOverdue ? 0.75 : 1,
      }}
    >
      <CardContent sx={{ py: '8px !important', px: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {isIncome ? (
              <TrendingUpIcon color="success" fontSize="small" />
            ) : (
              <TrendingDownIcon color="error" fontSize="small" />
            )}
            <Box>
              <Typography variant="body2" fontWeight={600} noWrap>
                {event.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(event.date)}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.5}>
            {isOverdue && (
              <Chip
                label="Overdue"
                size="small"
                color="warning"
                icon={<WarningAmberIcon />}
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
            )}
            <Typography
              variant="body2"
              fontWeight={700}
              color={isIncome ? 'success.main' : 'error.main'}
            >
              {isIncome ? '+' : '-'}
              {formatCurrency(event.amount)}
            </Typography>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

/** Custom recharts tooltip */
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <Paper elevation={3} sx={{ p: 1.5, minWidth: 170 }}>
      <Typography variant="caption" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      {payload.map((p) => (
        <Box key={p.dataKey} display="flex" justifyContent="space-between" gap={2}>
          <Typography variant="caption" color={p.color}>
            {p.name}
          </Typography>
          <Typography variant="caption" fontWeight={600}>
            {formatCurrency(p.value)}
          </Typography>
        </Box>
      ))}
    </Paper>
  );
}

// ─── main page ────────────────────────────────────────────────────────────────

export default function CashflowPage() {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── state ──────────────────────────────────────────────────────────────────
  const [months,        setMonths]        = useState(3);
  const [upcoming,      setUpcoming]      = useState([]);
  const [summary,       setSummary]       = useState([]);
  const [forecast,      setForecast]      = useState(null); // { startingBalance, days }
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  // ── fetch ───────────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [upRes, sumRes, foreRes] = await Promise.all([
        api.get('/cashflow/upcoming?days=14'),
        api.get(`/cashflow/summary?months=${months}`),
        api.get(`/cashflow/forecast?months=${months}`),
      ]);
      setUpcoming(upRes.data?.data  ?? []);
      setSummary(sumRes.data?.data  ?? []);
      setForecast(foreRes.data?.data ?? null);
      setLastRefreshed(new Date());
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load forecast data');
    } finally {
      setLoading(false);
    }
  }, [months]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── chart data ──────────────────────────────────────────────────────────────
  const chartData = React.useMemo(() => {
    if (!forecast?.days) return [];
    // Thin out for mobile — every 3rd day; desktop every other day
    const step = isMobile ? 3 : 2;
    return forecast.days
      .filter((_, i) => i % step === 0)
      .map((d) => ({
        date:      formatDate(d.date),
        balance:   d.runningBalance,
        upper:     Math.round(d.runningBalance * 1.1 * 100) / 100,
        lower:     Math.round(d.runningBalance * 0.9 * 100) / 100,
        rawDate:   d.date,
      }));
  }, [forecast, isMobile]);

  const todayIndex = chartData.findIndex((d) => d.rawDate >= todayStr);
  const todayLabel = todayIndex >= 0 ? chartData[todayIndex]?.date : null;

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1100, mx: 'auto' }}>
      {/* ── Header ── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        mb={2}
        spacing={1}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <AccountBalanceWalletIcon color="primary" />
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Cash-flow Forecast
            </Typography>
            {lastRefreshed && (
              <Typography variant="caption" color="text.secondary">
                Updated {lastRefreshed.toLocaleTimeString()}
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          <ToggleButtonGroup
            value={months}
            exclusive
            onChange={(_, v) => v && setMonths(v)}
            size="small"
          >
            {[1, 2, 3].map((m) => (
              <ToggleButton key={m} value={m}>
                {m}M
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Tooltip title="Refresh">
            <IconButton onClick={fetchAll} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!loading && (
        <>
          {/* ── Section 1: Upcoming Bills ── */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                <EventNoteIcon color="action" fontSize="small" />
                <Typography variant="h6" fontWeight={600}>
                  Next 14 Days
                </Typography>
                <Chip label={upcoming.length} size="small" color="primary" />
              </Stack>

              {upcoming.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No scheduled transactions in the next 14 days.
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
                  {upcoming.map((ev, i) => (
                    <UpcomingEventCard key={`${ev.date}-${ev.name}-${i}`} event={ev} />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* ── Section 2: Monthly Summary Table ── */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={1.5}>
                Monthly Projection
              </Typography>

              {summary.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No summary data available.
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { fontWeight: 700, whiteSpace: 'nowrap' } }}>
                        <TableCell>Month</TableCell>
                        <TableCell align="right">Income</TableCell>
                        <TableCell align="right">Expenses</TableCell>
                        <TableCell align="right">Net Change</TableCell>
                        <TableCell align="right">Ending Balance</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {summary.map((row) => {
                        const negBalance = row.endingBalance < 0;
                        const negNet     = row.netChange    < 0;
                        return (
                          <TableRow
                            key={row.month}
                            hover
                            sx={negBalance ? { bgcolor: 'error.50' } : {}}
                          >
                            <TableCell sx={{ fontWeight: 500 }}>{formatMonth(row.month)}</TableCell>
                            <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>
                              {formatCurrency(row.projectedIncome)}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>
                              {formatCurrency(row.projectedExpense)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: negNet ? 'error.main' : 'success.main', fontWeight: 600 }}
                            >
                              {negNet ? '' : '+'}
                              {formatCurrency(row.netChange)}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: 700,
                                color: negBalance ? 'error.main' : 'text.primary',
                              }}
                            >
                              {formatCurrency(row.endingBalance)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* ── Section 3: Running Balance Chart ── */}
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                <Typography variant="h6" fontWeight={600}>
                  Running Balance
                </Typography>
                {forecast && (
                  <Chip
                    label={`Start: ${formatCurrency(forecast.startingBalance)}`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                Shaded band = ±10% variable-spend uncertainty
              </Typography>

              {chartData.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No chart data available.
                </Typography>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="balGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={theme.palette.primary.main} stopOpacity={0.25} />
                        <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0.02} />
                      </linearGradient>
                      <linearGradient id="bandGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={theme.palette.primary.light} stopOpacity={0.15} />
                        <stop offset="95%" stopColor={theme.palette.primary.light} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>

                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />

                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tickFormatter={(v) => `€${(v / 1000).toFixed(1)}k`}
                      tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                      width={60}
                    />

                    <RechartsTooltip content={<CustomTooltip />} />
                    <Legend
                      wrapperStyle={{ fontSize: 12 }}
                      formatter={(value) =>
                        value === 'balance'
                          ? 'Projected Balance'
                          : value === 'upper'
                          ? '+10% Band'
                          : '−10% Band'
                      }
                    />

                    {/* Uncertainty band */}
                    <Area
                      type="monotone"
                      dataKey="upper"
                      stroke="none"
                      fill="url(#bandGrad)"
                      fillOpacity={1}
                      dot={false}
                      activeDot={false}
                      legendType="none"
                      name="upper"
                    />
                    <Area
                      type="monotone"
                      dataKey="lower"
                      stroke="none"
                      fill={theme.palette.background.paper}
                      fillOpacity={1}
                      dot={false}
                      activeDot={false}
                      legendType="none"
                      name="lower"
                    />

                    {/* Main balance line */}
                    <Area
                      type="monotone"
                      dataKey="balance"
                      stroke={theme.palette.primary.main}
                      strokeWidth={2}
                      strokeDasharray="5 3"
                      fill="url(#balGrad)"
                      dot={false}
                      activeDot={{ r: 4 }}
                      name="balance"
                    />

                    {/* Today marker */}
                    {todayLabel && (
                      <ReferenceLine
                        x={todayLabel}
                        stroke={theme.palette.warning.main}
                        strokeWidth={2}
                        label={{
                          value: 'Today',
                          position: 'top',
                          fontSize: 11,
                          fill: theme.palette.warning.main,
                        }}
                      />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
